import React from 'react';

import { CustomerAuthLevel } from 'models/Customer';
import { Article, AtlStatusValue } from 'models/shipments';
import { trackInteraction } from 'store/analytics/analytics.thunks';
import { selectAuthLevel } from 'store/customer';
import { useAppDispatch, useAppSelector } from 'store/store';
import { AppLocation, getAtlLink } from 'utils/window.utils';

import { extLnkMyPostRedirectV2 } from '../../../../config/track-config';
import RedirectModal from '../../common/RedirectModal/RedirectModal';
import { useCurrentArticle, useFirstArticle } from '../../hooks/shipment.hooks';
import AccordionWrapper from '../AccordionWrapper/AccordionWrapper';
import ButtonRow from '../RowCard/ButtonRow/ButtonRow';
import LinkRow from '../RowCard/LinkRow/LinkRow';
import RowCard from '../RowCard/RowCard';
import { ROW_CARD_ICONS } from '../RowCard/RowContent/RowContent';
import {
  DELIVERY_OPTIONS_TEST_ID,
  REDIRECT_OPTIONS_ROW_TEST_ID,
  SAFE_DROP_OPTIONS_ROW_TEST_ID,
} from './DeliveryOptions.test-ids';

const DeliveryOptions: React.FC = () => {
  const dispatch = useAppDispatch();
  const selectedArticle = useCurrentArticle();
  const firstArticle = useFirstArticle();
  const authLevel = useAppSelector(selectAuthLevel);

  const [isRedirectModalOpen, setIsRedirectModalOpen] = React.useState(false);

  if (!selectedArticle) return null;
  const heading = 'Delivery options';

  const getRedirectOptions = (article: Article) => {
    const { articleId, articleRedirect } = article;
    if (!articleId || !articleRedirect) return null;

    return (
      <>
        <ButtonRow
          icon={ROW_CARD_ICONS.redirect}
          text='Redirect it'
          onClick={() => {
            dispatch(trackInteraction('request redirect from nav'));
            setIsRedirectModalOpen(true);
          }}
          data-testid={REDIRECT_OPTIONS_ROW_TEST_ID}
        />
        <RedirectModal
          isOpen={isRedirectModalOpen}
          handleClose={() => setIsRedirectModalOpen(false)}
          handleContinue={() => AppLocation.assign(`${extLnkMyPostRedirectV2}/${articleId}`)}
        />
      </>
    );
  };

  const getSafeDropOptions = (article: Article, authLevel: CustomerAuthLevel) => {
    const { articleId, atlEligible, atlStatus } = article;
    if (!atlEligible) return null;

    const atlRequested = atlStatus === AtlStatusValue.REQUESTED;
    const atlLink = getAtlLink(authLevel, articleId);

    const safeDropRowText = atlRequested ? 'Change leave request' : 'Leave it in a safe place';

    return (
      <LinkRow
        icon={ROW_CARD_ICONS.location}
        text={safeDropRowText}
        href={atlLink}
        data-testid={SAFE_DROP_OPTIONS_ROW_TEST_ID}
        onClick={() => dispatch(trackInteraction(`${safeDropRowText.toLowerCase()} from nav`))}
      />
    );
  };

  const safeDropOptions = getSafeDropOptions(selectedArticle, authLevel);
  const redirectOptions = getRedirectOptions(firstArticle);

  if (!safeDropOptions && !redirectOptions) return null;

  const deliveryOptions = (
    <>
      {safeDropOptions}
      {redirectOptions}
    </>
  );

  return (
    <AccordionWrapper heading={heading} id='accordionDeliveryDptions'>
      <RowCard groupHeading={heading} data-testid={DELIVERY_OPTIONS_TEST_ID}>
        {deliveryOptions}
      </RowCard>
    </AccordionWrapper>
  );
};

export default DeliveryOptions;
